import { getPreferredPhone, useGetAllContacts } from "@api/contactService";
import { useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import { useGetAllUsers } from "@api/userService";
import { Alert, Autocomplete, AutocompleteOption, Button, Checkbox, Grid, ListItemContent, ListItemDecorator, Stack, Textarea } from "@mui/joy";
import { Link } from "react-router-dom";
import { ContextualHelp } from "@components/ContextualHelp";
import { countries, userRoles } from "@utils/selectOptions";
import Subheader from "@components/layout/Subheader";
import { FormProvider, useForm } from "react-hook-form";
import { ObjectPrimaryInfoEdit } from "@components/objects/ObjectPrimaryInfoEdit";
import ObjectColumn from "@components/objects/ObjectColumn";
import { useTenant } from "@providers/TenantProvider";
import { authenticatedApi, baseUrl, communityUrl } from "api";
import toast from "react-hot-toast";
import { useGetAllCoordinatingPrograms } from "@api/coordinatingProgramService";
import MaestroLoader from "@components/MaestroLoader";
import { formatDateForDatabase } from "@utils/dates";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import { useCopyToClipboard, useDocumentTitle } from "@uidotdev/usehooks";

const ScriptOutputField = ({settings}) => {
    const { tenant } = useTenant();
    const [copiedText, copyToClipboard] = useCopyToClipboard();
    const hasCopiedText = Boolean(copiedText);

    const includeScript = `<script>!function(m,a,e,s,t,r,o){var f=m.getElementsByTagName(a)[0],j=m.createElement(a);j.async=!0;j.src="${communityUrl.replace('%subdomain%', 'www')}/widget.js";f.parentNode.insertBefore(j,f)}(document, "script")</script>`;

        let dataAttrs = [
            `data-community="${tenant.subdomain}"`,
            `data-range="${settings.range}"`,
            `data-show-icons="${settings.icons ? 'yes' : 'no'}"`,
            `data-styles="${settings.styles ? 'yes' : 'no'}"`,
        ];

    const widgetCode = `<div mm-widget ${dataAttrs.join(' ')}></div>`;

    const performCopy = (content) => {
        copyToClipboard(content);
        toast.success('Copied to clipboard!');
    };
    // useCopyToClipboard
    // toast
    return (
        <Stack
        sx={theme => ({
            'code': {
                px: 0.75,
                py: 0.25,
                boxShadow: 'sm',
                borderRadius: 'sm',
                background: theme.palette.background.surface,
            }
        })}
        >
            <Typography
                sx={{
                    fontSize: 'md',
                    fontWeight: 'bold',
                    mt: 1,
                    mb: 1,
                }}
            >
                1. Add this to your <code>&lt;head&gt;</code> or <code>&lt;body&gt;</code> tag
            </Typography>
            <Textarea readOnly value={includeScript} onFocus={()=>performCopy(includeScript)}/>
            <Typography
                sx={{
                    fontSize: 'md',
                    fontWeight: 'bold',
                    mt: 2,
                    mb: 1,
                }}
            >
                2. Add this where you want to display the widget
            </Typography>
            <Textarea readOnly value={widgetCode} onFocus={()=>performCopy(widgetCode)}/>
        </Stack>
    )
}

const WidgetPreview = ({settings}) => {
    const { tenant } = useTenant();
    const [widgetResponse, setWidgetResponse] = useState('');

    useEffect(() => {
        const params = new URLSearchParams({
            community: tenant.subdomain,
            range: settings.range,
            icons: settings.icons ? 'yes' : 'no',
            styles: settings.styles ? 'yes' : 'no',
        });
      authenticatedApi.get(`/widget?${params}`).then(response => setWidgetResponse(response.data));
    }, [settings]);

    return (
        <div dangerouslySetInnerHTML={{__html: widgetResponse}}/>
    )
}

const widgetFields = [
    {
        name: 'range',
        label: 'Date Range',
        type: 'segmented', // radio
        options: [
            {label: 'All Time', value: 'all'},
            {label: 'Year to Date', value: 'ytd'},
        ],
    },
    {
        name: 'icons',
        label: 'Icons',
        type: 'boolean',
        inputLabel: 'Include Icons?',
    },
    {
        name: 'styles',
        label: 'Styles',
        type: 'boolean',
        inputLabel: 'Include Basic Styles?',
        // If you do not do this, you will be responsible for making sure the widget looks good on your website
    },
    {
        name: 'output',
        label: false,
        type: 'info',
        content: (settings) => <ScriptOutputField settings={settings} />
    }
    // styling
    // code gen
];

export default function StatWidgetGenerator() {
    useDocumentTitle(`Widgets | Maestro Community Manager`);

    // const { tenant, setTenant } = useTenant();

    const formMethods = useForm({
        defaultValues: {
            range: 'all',
            icons: false,
            styles: false,
        },
    });
    const {
        setValue,
        reset,
        handleSubmit,
        watch,
        formState: { isSubmitting, errors }
    } = formMethods;

    const onSubmit = (fields) => {
        console.log(fields);
    };

    const orgInfo = {};

    return (<>
        <Subheader
            title={'Widgets'}
            tint={'default'}
        >
        </Subheader>

        <Box sx={theme => ({
            // backgroundImage: `linear-gradient(180deg, ${theme.palette['default'][100]} 0%, white 100%);`,// : null,
            backgroundAttachment: 'fixed',
            minHeight: 'calc(100% - var(--Header-height))',
            p: 2,
        })}>
            <form onSubmit={handleSubmit(onSubmit)} initialData={orgInfo}>
                <FormProvider {...formMethods}>
                    <Grid container columns={12} gap={2}>
                        <ObjectColumn size={{xs: 12, md: 6, lg: 4}}>
                            <ObjectPrimaryInfoEdit
                                title={'Configure'}
                                tint={'default'}
                                fields={widgetFields}
                            />
                        </ObjectColumn>
                        <ObjectColumn size={{xs: 12, md: 6, lg: true}}>
                            <AbstractObjectRelationship title="Preview" sx={{'--mm-card-height': 'auto'}}>
                                <Box p={2}>
                                    <WidgetPreview settings={watch()}/>
                                </Box>
                            </AbstractObjectRelationship>
                                <Alert
                                    color="warning"
                                    variant="soft"
                                    sx={{
                                        mb: 1
                                    }}
                                >
                                    <Typography><b>Note:</b> We cannot guarantee that the widget will look the same on your website. Reach out to your web developer with any questions.</Typography>
                                </Alert>
                        </ObjectColumn>
                    </Grid>
                </FormProvider>
            </form>
        </Box>
    </>);
}
